import { useState, useEffect } from 'react';
import { X, ShoppingCart, AlertCircle, Calculator, Package, ChevronDown, Check } from 'lucide-react';
import ProductSelector from '../sales/ProductSelector';
import CustomerAutocomplete from '../sales/CustomerAutocomplete';
import ReferrerAutocomplete from '../sales/ReferrerAutocomplete';
import PaymentMethodSelector from '../sales/PaymentMethodSelector';
import { createSale } from '../../api/sales';
import { getProductById } from '../../api/products';
import { getConfiguredProducts } from '../../api/calculator';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const ConvertToSaleModal = ({ calculation, onClose, onSuccess }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [referrer, setReferrer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [amountPaid, setAmountPaid] = useState(0);
  const [saleDiscount, setSaleDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [autoPopulateLoading, setAutoPopulateLoading] = useState(false);
  
  // State for breakdown product selection
  const [breakdownProducts, setBreakdownProducts] = useState([]);
  const [breakdownProductOptions, setBreakdownProductOptions] = useState({});
  const [loadingBreakdownOptions, setLoadingBreakdownOptions] = useState({});
  // Initialize breakdown products and fetch options for non-selected breakdowns
  useEffect(() => {
    const initializeBreakdowns = async () => {
      if (!calculation?.results || calculation.results.length === 0) return;
      
      setAutoPopulateLoading(true);
      
      try {
        const initialBreakdowns = [];
        const loadedProducts = [];
        
        for (let i = 0; i < calculation.results.length; i++) {
          const result = calculation.results[i];
          
          // Extract tile dimensions from the result
          const tileDimensions = result.tile_dimensions || '';
          const match = tileDimensions.match(/(\d+)mm\s*×\s*(\d+)mm/);
          const tileLengthMm = match ? parseInt(match[1]) : null;
          const tileWidthMm = match ? parseInt(match[2]) : null;
          
          const breakdown = {
            index: i,
            size: result.size,
            tile_dimensions: result.tile_dimensions,
            tile_length_mm: tileLengthMm,
            tile_width_mm: tileWidthMm,
            cartons_needed: result.cartons_needed || 1,
            pieces_per_carton: result.pieces_per_carton,
            area_sqm: result.area_sqm,
            selectedProduct: null
          };
          
          // If product_id exists, load the product details
          if (result.product_id) {
            try {
              const response = await getProductById(result.product_id);
              if (response && response.id) {
                const product = response;
                breakdown.selectedProduct = {
                  product_id: product.id,
                  product_code: product.product_code,
                  product_name: product.name,
                  product_type: product.product_type || 'tile',
                  cost_price: parseFloat(product.cost_price),
                  selling_price: parseFloat(product.selling_price),
                  original_selling_price: parseFloat(product.selling_price),
                  quantity: result.cartons_needed || 1,
                  stock_available: product.stock_quantity,
                  unit_discount: 0,
                  unit: product.unit || 'cartons'
                };
                loadedProducts.push(breakdown.selectedProduct);
              }
            } catch (err) {
              console.error(`Failed to load product ${result.product_id}:`, err);
            }
          } else if (tileLengthMm && tileWidthMm) {
            // Fetch matching products for this tile size
            try {
              setLoadingBreakdownOptions(prev => ({ ...prev, [i]: true }));
              console.log(`Fetching products for breakdown ${i}: ${tileLengthMm}mm x ${tileWidthMm}mm`);
              const response = await getConfiguredProducts({
                tile_length_mm: tileLengthMm,
                tile_width_mm: tileWidthMm,
                limit: 100
              });
              console.log(`Response for breakdown ${i}:`, response);
              
              // Handle response - API returns { success: true, data: products[] }
              const products = response?.data || response?.products || [];
              console.log(`Products found for breakdown ${i}:`, products.length);
              
              if (products.length > 0) {
                setBreakdownProductOptions(prev => ({
                  ...prev,
                  [i]: products
                }));
              }
            } catch (err) {
              console.error(`Failed to fetch products for breakdown ${i}:`, err);
            } finally {
              setLoadingBreakdownOptions(prev => ({ ...prev, [i]: false }));
            }
          }
          
          initialBreakdowns.push(breakdown);
        }
        
        setBreakdownProducts(initialBreakdowns);
        
        if (loadedProducts.length > 0) {
          setSelectedProducts(loadedProducts);
          toast.success(`${loadedProducts.length} product(s) auto-selected from calculation`);
        }
      } catch (error) {
        console.error('Failed to initialize breakdowns:', error);
      } finally {
        setAutoPopulateLoading(false);
      }
    };
    
    initializeBreakdowns();
  }, [calculation]);

  // Handle product selection for a specific breakdown
  const handleBreakdownProductSelect = (breakdownIndex, product) => {
    // Update the breakdown with selected product
    const updatedBreakdowns = breakdownProducts.map((breakdown, idx) => {
      if (idx === breakdownIndex) {
        const selectedProd = product ? {
          product_id: product.id,
          product_code: product.product_code,
          product_name: product.name,
          product_type: product.product_type || 'tile',
          cost_price: parseFloat(product.cost_price || 0),
          selling_price: parseFloat(product.selling_price || 0),
          original_selling_price: parseFloat(product.selling_price || 0),
          quantity: breakdown.cartons_needed || 1,
          stock_available: product.stock_quantity,
          unit_discount: 0,
          unit: product.unit || 'cartons'
        } : null;
        return { ...breakdown, selectedProduct: selectedProd };
      }
      return breakdown;
    });
    
    setBreakdownProducts(updatedBreakdowns);
    
    // Sync with selectedProducts array for sale creation
    const allSelectedProducts = updatedBreakdowns
      .filter(b => b.selectedProduct)
      .map(b => b.selectedProduct);
    setSelectedProducts(allSelectedProducts);
  };

  // Check if all breakdowns have products selected
  const allBreakdownsHaveProducts = () => {
    return breakdownProducts.length > 0 && breakdownProducts.every(b => b.selectedProduct);
  };

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = selectedProducts.reduce((sum, product) => {
      return sum + ((product.selling_price - product.unit_discount) * product.quantity);
    }, 0);

    const totalDiscount = selectedProducts.reduce((sum, product) => {
      return sum + (product.unit_discount * product.quantity);
    }, 0) + saleDiscount;

    const totalAmount = subtotal - saleDiscount;

    const totalCost = selectedProducts.reduce((sum, product) => {
      return sum + (product.cost_price * product.quantity);
    }, 0);

    const grossProfit = totalAmount - totalCost;

    // Commission calculation
    let commissionAmount = 0;
    if (referrer && referrer.commission_rate) {
      commissionAmount = (grossProfit * referrer.commission_rate) / 100;
    }

    const netProfit = grossProfit - commissionAmount;

    return {
      subtotal,
      totalDiscount,
      totalAmount,
      totalCost,
      grossProfit,
      commissionAmount,
      netProfit
    };
  };

  const totals = calculateTotals();

  const formatCurrency = (amount) => {
    return `₦${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate products based on calculation type
    const allPreSelected = calculation?.results?.length > 0 && calculation.results.every(r => r.product_id);
    
    if (allPreSelected) {
      // For calculations where ALL products are pre-selected, use ProductSelector validation
      if (selectedProducts.length === 0) {
        toast.error('Please add at least one product to the sale');
        return;
      }
    } else {
      // For mixed or empty calculations, all breakdowns must have products selected
      if (!allBreakdownsHaveProducts()) {
        toast.error('Please select a product for each tile size breakdown');
        return;
      }
    }

    if (!customer) {
      toast.error('Please select a customer');
      return;
    }

    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    try {
      setLoading(true);

      const saleData = {
        customer: {
          customer_id: customer.customer_id,
          full_name: customer.full_name,
          phone: customer.phone,
          email: customer.email,
          address: customer.address,
          city: customer.city,
          state: customer.state
        },
        items: selectedProducts.map(product => ({
          product_id: product.product_id,
          quantity: product.quantity,
          selling_price: product.selling_price,
          unit_discount: product.unit_discount || 0
        })),
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        amount_paid: amountPaid,
        discount: {
          type: 'fixed',
          value: saleDiscount
        },
        sale_notes: notes.trim() || null
      };

      // Add referrer if selected
      if (referrer) {
        saleData.referrer = {
          referrer_id: referrer.referrer_id,
          full_name: referrer.full_name,
          phone: referrer.phone,
          email: referrer.email,
          commission_rate: referrer.commission_rate
        };
      }

      const response = await createSale(saleData);

      toast.success(`Sale created successfully! Receipt: ${response.data.receipt_number}`);
      onSuccess(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create sale');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[95vh] flex flex-col shadow-xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-lightGray flex-none bg-white z-10 rounded-t-xl">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-primary-black">
            <ShoppingCart className="w-6 h-6 text-accent-gold" />
            Convert Calculation to Sale
          </h2>
          <button
            className="w-9 h-9 flex items-center justify-center bg-secondary-offWhite rounded-lg text-primary-slate hover:bg-red-50 hover:text-red-500 transition-colors"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Calculation Reference */}
        <div className="px-6 py-3 bg-accent-gold/5 border-b border-secondary-lightGray flex-none">
          <div className="flex items-start gap-2">
            <Calculator className="w-5 h-5 text-accent-gold mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-sm font-semibold text-primary-black">Calculation Reference:</span>
                <span className="font-mono text-sm text-accent-gold font-semibold">{calculation.calculation_number}</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-xs">
                <div>
                  <span className="text-primary-slate">Tile Sizes: </span>
                  <span className="text-primary-black font-medium">
                    {calculation.results?.map(r => r.size).join(', ') || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-primary-slate">Total Area: </span>
                  <span className="text-primary-black font-medium">{calculation.total_area_sqm?.toFixed(2) || '0'} m²</span>
                </div>
                <div>
                  <span className="text-primary-slate">Total Cartons Needed: </span>
                  <span className="text-primary-black font-medium">
                    {calculation.results?.reduce((sum, r) => sum + (r.cartons_needed || 0), 0) || 0} cartons
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Scrollable */}
        <div className="p-6 flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Products */}
            <div className="lg:col-span-2 space-y-6">
              {/* Product Selection */}
              <div className="bg-white border border-secondary-lightGray rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-primary-black">
                    Products <span className="text-red-500">*</span>
                  </h3>
                  {autoPopulateLoading && (
                    <div className="flex items-center gap-2 text-xs text-accent-gold">
                      <div className="w-4 h-4 border-2 border-accent-gold/30 border-t-accent-gold rounded-full animate-spin" />
                      Loading products from calculation...
                    </div>
                  )}
                </div>
                
                {/* Show ProductSelector for calculations where ALL products are pre-selected */}
                {calculation?.results?.length > 0 && calculation.results.every(r => r.product_id) ? (
                  <>
                    {/* Auto-populated products indicator */}
                    {selectedProducts.length > 0 && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-center gap-2">
                        <Package className="w-5 h-5 text-amber-600" />
                        <span className="text-sm text-amber-800 font-medium">
                          {selectedProducts.length} product(s) auto-selected from calculation. Adjust quantities as needed.
                        </span>
                      </div>
                    )}
                    
                    <ProductSelector
                      onProductsChange={setSelectedProducts}
                      selectedProducts={selectedProducts}
                    />
                    <p className="mt-3 text-sm text-gray-500">
                      💡 Select tiles/products from your inventory to fulfill this calculation
                    </p>
                  </>
                ) : (
                  <>
                    {/* Breakdown cards for mixed or empty calculations */}
                    {breakdownProducts.length === 0 && !autoPopulateLoading ? (
                      <div className="text-center py-8 text-gray-500">
                        <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No calculation breakdowns found</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {breakdownProducts.map((breakdown, idx) => (
                          <div 
                            key={idx}
                            className={`p-4 rounded-lg border-2 bg-gray-50 ${
                              breakdown.selectedProduct 
                                ? 'border-gray-300' 
                                : 'border-red-400'
                            }`}
                          >
                            {/* Breakdown Header */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-gray-800">
                                  {breakdown.size}
                                </span>
                                {breakdown.tile_dimensions && (
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                    {breakdown.tile_dimensions}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs">
                                <span className="font-medium text-gray-600">
                                  {breakdown.cartons_needed} cartons
                                </span>
                                {breakdown.area_sqm && (
                                  <span className="text-gray-500">
                                    {breakdown.area_sqm.toFixed(2)} m²
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Product Selector or Selected Product Display */}
                            {breakdown.selectedProduct ? (
                              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                                <div className="flex items-center gap-3">
                                  <Check className="w-5 h-5 text-green-600" />
                                  <div>
                                    <span className="text-sm font-mono text-gray-800 font-bold">
                                      {breakdown.selectedProduct.product_code}
                                    </span>
                                    <span className="text-sm text-gray-700 ml-2">
                                      {breakdown.selectedProduct.product_name}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleBreakdownProductSelect(idx, null)}
                                  className="text-sm text-gray-500 hover:text-red-600 font-medium"
                                >
                                  Change
                                </button>
                              </div>
                            ) : (
                              <div>
                                {loadingBreakdownOptions[idx] ? (
                                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                                    <div className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-500 rounded-full animate-spin" />
                                    Loading matching products...
                                  </div>
                                ) : breakdownProductOptions[idx]?.length > 0 ? (
                                  <div className="relative">
                                    <select
                                      className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 appearance-none cursor-pointer"
                                      defaultValue=""
                                      onChange={(e) => {
                                        const productId = e.target.value;
                                        const product = breakdownProductOptions[idx]?.find(p => p.id.toString() === productId);
                                        if (product) {
                                          handleBreakdownProductSelect(idx, product);
                                        }
                                      }}
                                    >
                                      <option value="" className="text-gray-500">⚠️ Select a product for this tile size...</option>
                                      {breakdownProductOptions[idx].map(product => (
                                        <option key={product.id} value={product.id} className="text-gray-800">
                                          {product.product_code} - {product.name} (₦{parseFloat(product.selling_price).toLocaleString()})
                                        </option>
                                      ))}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                    <span>No products found matching {breakdown.tile_dimensions}. Please configure products with this tile size.</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Validation Message */}
                    {breakdownProducts.length > 0 && !allBreakdownsHaveProducts() && (
                      <div className="mt-4 p-3 rounded-lg flex items-center gap-2 border-2 border-red-500" style={{ backgroundColor: '#301926' }}>
                        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-white">
                          Please select a product for each tile size breakdown before creating the sale.
                        </span>
                      </div>
                    )}
                    
                    <p className="mt-4 text-sm text-gray-500">
                      💡 Products are filtered to match the exact tile sizes used in your calculation
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Right Column - Customer, Referrer, Summary, Payment */}
            <div className="space-y-4">
              {/* Customer */}
              <div className="bg-white border border-secondary-lightGray rounded-lg p-4">
                <h3 className="text-sm font-semibold text-primary-black mb-3">
                  Customer <span className="text-red-500">*</span>
                </h3>
                <CustomerAutocomplete
                  onCustomerChange={setCustomer}
                  selectedCustomer={customer}
                />
              </div>

              {/* Referrer (Optional) */}
              <div className="bg-white border border-secondary-lightGray rounded-lg p-4">
                <h3 className="text-sm font-semibold text-primary-black mb-3">
                  Referrer <span className="text-xs text-secondary-gray font-normal">(Optional)</span>
                </h3>
                <ReferrerAutocomplete
                  onReferrerChange={setReferrer}
                  selectedReferrer={referrer}
                />
              </div>

              {/* Sale Summary */}
              <div className="bg-white border border-secondary-lightGray rounded-lg p-4">
                <h3 className="text-sm font-semibold text-primary-black mb-3">Sale Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-secondary-gray">Subtotal:</span>
                    <span className="font-medium text-primary-black">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-gray">Product Discounts:</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(totals.totalDiscount - saleDiscount)}
                    </span>
                  </div>

                  {/* Additional Sale Discount */}
                  <div className="pt-2 border-t border-secondary-lightGray">
                    <label className="block text-xs text-secondary-gray mb-1">Additional Discount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray text-sm">₦</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={saleDiscount}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          setSaleDiscount(parseFloat(value) || 0);
                        }}
                        className="w-full pl-8 pr-4 py-2 bg-white border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="flex justify-between pt-2 border-t border-secondary-lightGray">
                    <span className="font-semibold text-primary-black">Total Amount:</span>
                    <span className="font-bold text-lg text-accent-gold">{formatCurrency(totals.totalAmount)}</span>
                  </div>

                {/* Profit Information */}
                {isAdmin && (
                  <div className="pt-3 border-t border-secondary-lightGray space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-secondary-gray">Cost:</span>
                      <span className="text-secondary-gray">{formatCurrency(totals.totalCost)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-secondary-gray">{totals.grossProfit >= 0 ? 'Gross Profit:' : 'Gross Loss:'}</span>
                      <span className={`font-medium ${totals.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(totals.grossProfit)}</span>
                    </div>
                    {referrer && (
                      <div className="flex justify-between text-xs">
                        <span className="text-secondary-gray">Commission ({referrer.commission_rate}%):</span>
                        <span className="text-yellow-600">-{formatCurrency(totals.commissionAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-primary-black">{totals.netProfit >= 0 ? 'Net Profit:' : 'Net Loss:'}</span>
                      <span className={totals.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}>{formatCurrency(totals.netProfit)}</span>
                    </div>
                  </div>
                )}
                </div>
              </div>

              {/* Payment */}
              <div className="bg-white border border-secondary-lightGray rounded-lg p-4">
                <h3 className="text-sm font-semibold text-primary-black mb-3">Payment</h3>
                <PaymentMethodSelector
                  paymentMethod={paymentMethod}
                  onPaymentMethodChange={setPaymentMethod}
                  paymentStatus={paymentStatus}
                  onPaymentStatusChange={setPaymentStatus}
                  amountPaid={amountPaid}
                  onAmountPaidChange={setAmountPaid}
                  totalAmount={totals.totalAmount}
                />
              </div>

              {/* Notes */}
              <div className="bg-white border border-secondary-lightGray rounded-lg p-4">
                <h3 className="text-sm font-semibold text-primary-black mb-3">
                  Notes <span className="text-xs text-secondary-gray font-normal">(Optional)</span>
                </h3>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold resize-none text-sm"
                  placeholder="Add any notes or special instructions..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 px-6 py-4 border-t border-secondary-lightGray bg-secondary-offWhite rounded-b-xl flex-none">
          <button
            type="button"
            className="flex-1 px-4 py-2.5 border border-secondary-lightGray text-primary-slate rounded-lg font-medium hover:bg-white transition-colors"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2.5 bg-accent-gold text-white rounded-lg font-semibold hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || selectedProducts.length === 0 || !customer}
            onClick={handleSubmit}
          >
            {loading ? 'Creating Sale...' : 'Complete Sale'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConvertToSaleModal;
